import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { Card } from '@components/atoms/ui-kit/card.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { DatasetValidationResult } from '@utils/api/custom-models/use-validate-custom-model.tsx';

interface Props {
  readiness: DatasetValidationResult | undefined;
  loading: boolean;
}

/**
 * Progress toward the training-readiness floor, always visible on the
 * custom-model page. #2211: "the current flow validates only when the user
 * presses Train, which is the worst possible moment to learn the dataset was
 * never eligible."
 *
 * Renders duration progress as a bar (not an error), counts by source,
 * distinct anchor terms, and blocking errors separated from warnings. The
 * train button on the page is gated on ``readiness.valid``; this card
 * explains *why* it is or is not enabled.
 */
export const ReadinessCard = ({ readiness, loading }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  if (loading && !readiness) {
    return (
      <Card padding="none" className="p-4">
        <Text as="p" size="sm" tone="muted">
          {t('readiness-loading')}
        </Text>
      </Card>
    );
  }

  if (!readiness) return null;

  const totalDuration = readiness.total_duration_seconds ?? 0;
  const requiredDuration = readiness.required_duration_seconds ?? 0;
  const hasDurationFloor = requiredDuration > 0;
  const durationPct = hasDurationFloor
    ? Math.min(100, Math.round((totalDuration / requiredDuration) * 100))
    : 100;

  const matched = readiness.matched_pairs ?? 0;
  const requiredPairs = readiness.required_pairs ?? 0;
  const uploaded = readiness.uploaded_files ?? 0;
  const clips = readiness.contributed_clips ?? readiness.clips_count ?? 0;
  const distinctTerms = readiness.distinct_anchor_terms ?? 0;

  const errors = readiness.errors ?? [];
  const warnings = readiness.warnings ?? [];
  const ready = readiness.valid;

  const barBg = getColour('thirdLayer');
  const barFg = ready ? getColour('success') : getColour('activeText');

  return (
    <Card padding="none" className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {ready ? (
            <CheckCircle2
              className="h-4 w-4"
              style={{ color: getColour('success') }}
              aria-hidden="true"
            />
          ) : (
            <Info
              className="h-4 w-4"
              style={{ color: getColour('activeText') }}
              aria-hidden="true"
            />
          )}
          <Text as="span" size="sm" weight="semibold">
            {t('dataset-readiness')}
          </Text>
        </div>
        <Text as="span" size="xs" tone="muted">
          {ready ? t('ready-to-train') : t('not-yet-ready-to-train')}
        </Text>
      </div>

      {/* Duration progress. Not rendered when the resumed-round path has
          waived the floor (requiredDuration = 0). */}
      {hasDurationFloor && (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Text as="span" size="xs" tone="muted">
              {t('training-audio-progress')}
            </Text>
            <Text as="span" size="xs" weight="medium">
              {t('readiness-duration', {
                current: formatMinutes(totalDuration),
                target: formatMinutes(requiredDuration)
              })}
            </Text>
          </div>
          <div
            className="h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: barBg }}
            role="progressbar"
            aria-valuenow={durationPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('training-audio-progress')}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${durationPct}%`, backgroundColor: barFg }}
            />
          </div>
        </div>
      )}

      {/* Counts by source + distinct terms. Sixty clips of one name teaches
          one word — the customer should be able to see that shape. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
        <ReadinessStat
          label={t('readiness-matched-pairs')}
          value={
            requiredPairs > 0
              ? `${matched} / ${requiredPairs}`
              : String(matched)
          }
        />
        <ReadinessStat
          label={t('readiness-uploads')}
          value={String(uploaded)}
        />
        <ReadinessStat
          label={t('readiness-contributed-clips')}
          value={String(clips)}
        />
        <ReadinessStat
          label={t('readiness-distinct-terms')}
          value={String(distinctTerms)}
          hint={
            distinctTerms === 1 && clips > 5
              ? t('readiness-distinct-terms-narrow')
              : undefined
          }
        />
      </div>

      {errors.length > 0 && (
        <ul className="space-y-1 pt-1">
          {errors.map((message, idx) => (
            <li key={idx} className="flex items-start gap-2 text-xs">
              <AlertCircle
                className="h-3.5 w-3.5 mt-0.5 shrink-0"
                style={{ color: getColour('danger') }}
                aria-hidden="true"
              />
              <span style={{ color: getColour('danger') }}>{message}</span>
            </li>
          ))}
        </ul>
      )}

      {warnings.length > 0 && (
        <ul className="space-y-1 pt-1">
          {warnings.map((message, idx) => (
            <li key={idx} className="flex items-start gap-2 text-xs">
              <Info
                className="h-3.5 w-3.5 mt-0.5 shrink-0"
                style={{ color: getColour('inactiveText') }}
                aria-hidden="true"
              />
              <span style={{ color: getColour('inactiveText') }}>
                {message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const ReadinessStat = ({
  label,
  value,
  hint
}: {
  label: string;
  value: string;
  hint?: string;
}) => (
  <div>
    <Text as="p" size="xs" tone="muted" className="truncate">
      {label}
    </Text>
    <Text as="p" size="sm" weight="semibold" className="mt-0.5">
      {value}
    </Text>
    {hint && (
      <Text as="p" size="xs" tone="muted" className="mt-0.5">
        {hint}
      </Text>
    )}
  </div>
);

const formatMinutes = (seconds: number): string => {
  if (seconds <= 0) return '0m';
  const totalMinutes = seconds / 60;
  if (totalMinutes < 1) {
    return `${Math.round(seconds)}s`;
  }
  if (totalMinutes < 10) {
    return `${totalMinutes.toFixed(1)}m`;
  }
  return `${Math.round(totalMinutes)}m`;
};
